// Look Up (LibraryModel+LookUp.swift; design-docs/Look Up — design.md, phase 5): what the project knows about a word in
// the text, answered in the inspector's Look Up tab. Resolution is the core's (`resolveLookUp`) over the open project:
// the cast, the glossary, the research notes in reading order and the manuscript's scenes, with the presence rows the
// app works out and a whole-word scan of each document's words standing in for the Mac's index. Local, never the web —
// the web is Pictures from Wikimedia Commons, by its own button (store/wikimedia.ts). The answer and the tab live here
// so the text's menu, the Cast page and the inspector share them.
import { useSyncExternalStore } from 'react';
import {
  lookUpHits, lookUpNames, lookUpQuery, resolveLookUp, sha256Hex, StoreEditor, tokenize, type DocumentEditor, type FragmentRecord, type LookUpAnswer,
  type LookUpQuery, type LookUpSources, type LookUpText, type OpenedProject, type Project, type ProjectEditor, type Token,
} from '@writing-suite/core';
import { readGlossary } from '@writing-suite/core/story';
import type { Api } from '@writing-suite/client/net/api.ts';
import { bodyText } from './binderFilter.ts';
import { uploadAttachments } from './importScrivener.ts';
import type { LiveProject } from './project.ts';
import { opened, storyModel } from './story.ts';
import { downloadPicture, fetchPictures, pictureCredit, preparePhoto, type WikiPicture } from './wikimedia.ts';

export type InspectorTab = 'details' | 'lookUp';

/** Pictures from Wikimedia for the answer's words: shown and forgotten, until Keep. */
export interface WebPictures {
  query: string | null;
  pictures: WikiPicture[];
  nextOffset: number | null;
  loading: boolean;
  error: string | null;
  kept: Set<string>;
  /** Where each kept picture went (its page or new note), for Show. */
  keptIn: Map<string, string>;
  keeping: Set<string>;
}

export interface LookUpState {
  answer: LookUpAnswer | null;
  tab: InspectorTab;
  /** Counts the explicit look-ups, so the shell opens the inspector on each. */
  requests: number;
  web: WebPictures;
}

const TAB_KEY = 'ws:inspectorTab';
const emptyWeb = (): WebPictures => ({ query: null, pictures: [], nextOffset: null, loading: false, error: null, kept: new Set(), keptIn: new Map(), keeping: new Set() });
const readTab = (): InspectorTab => { try { return localStorage.getItem(TAB_KEY) === 'lookUp' ? 'lookUp' : 'details'; } catch { return 'details'; } };

let state: LookUpState = { answer: null, tab: readTab(), requests: 0, web: emptyWeb() };
const listeners = new Set<() => void>();
function set(patch: Partial<LookUpState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export function useLookUp(): LookUpState {
  return useSyncExternalStore(subscribe, () => state, () => state);
}
export const lookUpState = () => state;

export function setInspectorTab(tab: InspectorTab) {
  try { localStorage.setItem(TAB_KEY, tab); } catch { /* private window */ }
  set({ tab });
}

// MARK: Sources and hits over the open project

/** The project as Look Up reads it (`LibraryModel.lookUpSources`). */
export function lookUpSources(project: Project): LookUpSources {
  const p = opened(project);
  const page = (id: string) => {
    const f = project.fragment(id);
    return f && !f.trashed ? { id, title: f.title, kind: f.kind } : null;
  };
  const notes: LookUpSources['notes'] = [];
  const seen = new Set<string>();
  const walk = (nodes: { fragmentId: string | null; children: any[] }[]) => {
    for (const n of nodes) {
      if (n.fragmentId && !seen.has(n.fragmentId)) {
        const made = page(n.fragmentId);
        if (made) { seen.add(n.fragmentId); notes.push(made); }
      }
      walk(n.children);
    }
  };
  for (const a of project.arrangements()) if (a.role === 'reference') walk(project.tree(a.id));
  const scenes = project.scenes().map((s) => page(s.id)).filter((x): x is NonNullable<typeof x> => x !== null);
  return { entities: p.record.entities, glossary: readGlossary(p.record), notes, scenes };
}

const tokenCache = new WeakMap<FragmentRecord, { body?: Token[]; notes?: Token[] }>();
function tokensOf(f: FragmentRecord, column: 'body' | 'notes'): Token[] {
  let c = tokenCache.get(f);
  if (!c) tokenCache.set(f, (c = {}));
  return (c[column] ??= tokenize(column === 'body' ? bodyText(f) : f.notes));
}

function* texts(p: OpenedProject): Iterable<LookUpText & { record: FragmentRecord }> {
  for (const id of p.fragmentIds()) {
    const f = p.fragmentRecord(id);
    if (f) yield { id, body: '', notes: '', record: f };
  }
}

/** The answer for a query over the project (`LibraryModel.resolveLookUp`). */
export function answerFor(project: Project, query: LookUpQuery): LookUpAnswer {
  const sources = lookUpSources(project);
  const { cast } = lookUpNames(query, sources);
  const p = opened(project);
  const hits = lookUpHits(query, cast, texts(p), storyModel(project).rows, (t, column) => tokensOf((t as unknown as { record: FragmentRecord }).record, column));
  return resolveLookUp(query, sources, hits);
}

/** Looks the words up and asks the shell to show the inspector's Look Up tab. False when there's nothing to look up. */
export function lookUp(project: Project, words: string): boolean {
  const query = lookUpQuery(words);
  if (!query) return false;
  const answer = answerFor(project, query);
  set({ answer, tab: 'lookUp', requests: state.requests + 1, web: answer.query.key === state.answer?.query.key ? state.web : emptyWeb() });
  try { localStorage.setItem(TAB_KEY, 'lookUp'); } catch { /* private window */ }
  return true;
}

/** The same words again, after the project changed (a page, a cast entry, a glossary word or a picture added). */
export function refreshLookUp(project: Project) {
  if (!state.answer) return;
  set({ answer: answerFor(project, state.answer.query) });
}

export function clearLookUp() {
  set({ answer: null, web: emptyWeb() });
}

// MARK: Pictures from Wikimedia

/** Asks Wikimedia for pictures of the answer's words: the one time Look Up sends anything, because the writer pressed. */
export async function findPictures(locale: string) {
  const query = state.answer?.query.text;
  if (!query || state.web.loading) return;
  set({ web: { ...emptyWeb(), query, loading: true } });
  await fetchMore(query, locale, 0);
}

export async function morePictures(locale: string) {
  const { query, nextOffset, loading } = state.web;
  if (!query || nextOffset === null || loading) return;
  set({ web: { ...state.web, loading: true, error: null } });
  await fetchMore(query, locale, nextOffset);
}

async function fetchMore(query: string, locale: string, offset: number) {
  try {
    const page = await fetchPictures(query, locale, offset);
    if (state.web.query !== query) return; // the answer moved on
    const known = new Set(state.web.pictures.map((p) => p.id));
    set({ web: { ...state.web, pictures: [...state.web.pictures, ...page.pictures.filter((p) => !known.has(p.id))], nextOffset: page.nextOffset, loading: false } });
  } catch (e) {
    if (state.web.query !== query) return;
    set({ web: { ...state.web, loading: false, error: e instanceof Error ? e.message : String(e) } });
  }
}

/** Downloads the picture and files it: on the first cast entity's study page, with its credit added to the page's
 *  notes; with no page, as a new note in the Inbox titled with the words, empty, the credit in its notes too (the
 *  picture shows above the note's text). The bytes go to the relay as an attachment snapshot sealed with the project
 *  key, as a Scrivener import's do. */
export async function keepPicture(live: LiveProject, api: Api, project: Project, picture: WikiPicture): Promise<string | null> {
  const answer = state.answer;
  if (!answer || state.web.keeping.has(picture.id)) return null;
  const keeping = new Set(state.web.keeping).add(picture.id);
  set({ web: { ...state.web, keeping } });
  try {
    const prepared = await preparePhoto(await downloadPicture(picture), picture.mimeType);
    const sha256 = sha256Hex(prepared.bytes);
    const filename = `${picture.title}.${prepared.extension}`;
    await uploadAttachments(api, live.id, live.key, [{ sha256, filename, mimeType: prepared.mimeType, bytes: prepared.bytes }], live.mergedCursor);
    const credit = pictureCredit(picture);
    const study = answer.cast.find((e) => e.studyID !== undefined)?.studyID;
    const target = live.edit((e: ProjectEditor) => {
      const d = e as DocumentEditor;
      const s = new StoreEditor(d.docs, d.context);
      const id = study ?? s.addDraft({ title: answer.query.text, text: '', notes: '' });
      s.addAttachment(id, { sha256, filename, mimeType: prepared.mimeType }, { noteLine: credit, message: 'Keep a picture from Wikimedia' });
      return id;
    });
    set({ web: { ...state.web, kept: new Set(state.web.kept).add(picture.id), keptIn: new Map(state.web.keptIn).set(picture.id, target), keeping: without(state.web.keeping, picture.id) } });
    refreshLookUp(project);
    return null;
  } catch (e) {
    set({ web: { ...state.web, keeping: without(state.web.keeping, picture.id) } });
    return e instanceof Error ? e.message : String(e);
  }
}

const without = (s: Set<string>, id: string) => { const n = new Set(s); n.delete(id); return n; };
